import {
  createTheme,
  Theme,
  TypographyVariantsOptions as TypographyOptions,
} from "@mui/material/styles";

const defaultTheme: Theme = createTheme();

/**
 * The platform's single, locked type scale: Manrope for every heading role
 * (`h1`–`h6`) and Lato for everything that reads as body or UI text
 * (`body1`, `body2`, `button`, `caption`, `overline`). This file is the only
 * place font choices are made, and every value below is stated exactly, not
 * derived from a ratio or a prior ramp.
 *
 * `subtitle1`/`subtitle2` are the one deliberate exception — see the comment
 * on `subtitle1` below. Putting them on the Manrope heading scale, as the
 * platform spec's literal text would, silently bloated the ~35 pre-existing
 * call sites written against their old compact Lato size, with no text of
 * theirs touched — the "older pages went massive" report. They keep their
 * pre-spec value instead.
 */
export const DISPLAY_FONT = "'Manrope', 'Lato', sans-serif";
const BODY_FONT = "'Lato', 'Roboto', sans-serif";

/**
 * No variant states `textAlign`. Eight of them used to carry
 * `textAlign: "left"` — defensively, against a global `text-align: center` that
 * no longer exists anywhere in `src/theme` or `src/css`. What it does now is
 * break every centred container: a variant's own class outranks the inherited
 * `text-align`, so a `Typography` inside a `textAlign: "center"` panel silently
 * went back to left while its unstyled siblings stayed centred (the AI critique
 * empty state read exactly that way — left title over centred body). Alignment
 * belongs to the box, so the box keeps it.
 */
const typography = {
  fontFamily: "'Lato', 'Roboto', sans-serif",
  /**
   * these typography values can be applied by 'light', 'regular', 'medium' and 'bold'
   * Usage:
   * =====
   *
     <Box sx={{ fontWeight: 'light' }}>
      theme.typography.fontWeightLight
     </Box>

     <Box sx={{ fontWeight: 'regular' }}>
      theme.typography.fontWeightRegular
     </Box>

     <Box sx={{ fontWeight: 'medium' }}>
      theme.typography.fontWeightMedium
     </Box>

     <Box sx={{ fontWeight: 500 }}>
       custom values
     </Box>

     <Box sx={{ fontWeight: 'bold' }}>
        theme.typography.fontWeightMedium
     </Box>
   *
   */
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  /** Page title. Manrope 600, 40px. */
  h1: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(40),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  /** Section heading and the hub header band's title. Manrope 600, 30px. */
  h2: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(30),
    fontWeight: 600,
    lineHeight: 1.25,
  },
  /** Card and in-page section title. Manrope 500, 22px. */
  h3: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(22),
    fontWeight: 500,
    lineHeight: 1.3,
  },
  /** Row / UI title. Manrope 500, 19px. */
  h4: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(19),
    fontWeight: 500,
    lineHeight: 1.35,
  },
  /** Fifth heading step. Manrope 500, 17px. */
  h5: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(17),
    fontWeight: 500,
    lineHeight: 1.4,
  },
  /**
   * An ordinary small heading, NOT the uppercase eyebrow — it has call sites
   * (Footer, Header, `Tags/Complete`, `BuilderTopicCard`) using it as a plain
   * small heading, and putting the eyebrow's uppercase and tracking on the
   * variant would shout every one of them. The eyebrow is `overline`.
   *
   * The bare-element uppercase rule (`h6:not(.MuiTypography-root)` in
   * `Override.tsx`) only ever applied to bare `<h6>` tags, never to this
   * variant, and is untouched.
   */
  h6: {
    fontFamily: DISPLAY_FONT,
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 600,
    lineHeight: 1.4,
  },
  /**
   * DELIBERATE EXCEPTION to the platform font spec — kept off the Manrope
   * heading scale on purpose. `subtitle1` is the entrenched compact
   * "field-label / status line" role: 29 call sites (`Field.tsx`, ~16
   * `InputLabel`s, tooltips, card status lines) written against 13px/700
   * Lato, several with their own comment stating that exact size
   * (`BuilderCard.tsx`: "Design guide's status line: 13px/700"). Moving it
   * onto the new 17px/500 Manrope heading size — as the platform spec's
   * literal `subtitle1` entry would — silently bloated every one of those
   * 29 legacy call sites without anyone touching their text, which is the
   * "older pages went massive" regression. Reverted here, at the theme
   * level, rather than editing 29 files: this variant carries the pre-spec
   * value so those call sites need no change at all.
   */
  subtitle1: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(13),
    lineHeight: 20 / 13,
    fontWeight: 700,
  },
  /** Same exception as `subtitle1`, for its 6 call sites: Lato 14/700, the
   * pre-spec value. */
  subtitle2: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 20 / 12,
    fontWeight: 700,
  },
  /** Body text. Lato 400, 15px. */
  body1: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  /** Footer / helper text. Colour comes from the `color` prop, never baked in. */
  body2: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5,
  },
  /** Button label. Lato 700, `textTransform: none` stated explicitly rather
   * than MUI's uppercase default. */
  button: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 700,
    lineHeight: 1.2,
    textTransform: "none",
  },
  /**
   * Field-level error. Also MUI's `FormHelperText` base style — it spreads
   * `theme.typography.caption` directly (see `FormHelperText.js`) — so every
   * field's validation message renders through this variant.
   */
  caption: {
    display: "inline-block",
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
    lineHeight: 1.4,
  },
  /**
   * The eyebrow. Lato 700, 11px, 0.08em tracking, uppercase.
   *
   * `textTransform` is on the variant, unlike every other variant here: an
   * eyebrow that is not uppercase is not an eyebrow.
   */
  overline: {
    fontFamily: BODY_FONT,
    fontSize: defaultTheme.typography.pxToRem(11),
    fontWeight: 700,
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  /** Renders as whatever contains it — states its family explicitly so an
   * inherited Manrope or Lato from a parent variant carries through as-is. */
  inherit: {
    fontFamily: "inherit",
  },
} as TypographyOptions;

export default typography;
